import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import AdminShell from "../components/admin/AdminShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Search, RefreshCw, Loader2, UserPlus, Download, HeartPulse, X,
  ChevronLeft, ChevronRight, Globe, Smartphone, Monitor, HelpCircle,
} from "lucide-react";
import UserDrawer from "../components/admin/UserDrawer";
import { COUNTRIES } from "../utils/countries";
import { useDebounce } from "../utils/search";

const API = "/api";

const COMMERCIAL_OPTIONS = [
  ["active", "Membresía activa"],
  ["payment_pending", "Pago pendiente"],
  ["payment_under_review", "Pago en revisión"],
  ["renewal_under_review", "Renovación en revisión"],
  ["payment_rejected", "Pago rechazado"],
  ["expired", "Membresía vencida"],
  ["suspended", "Membresía suspendida"],
  ["none", "Sin membresía"],
];

const COMMUNICATION_OPTIONS = [
  ["operational", "Push operativo"],
  ["no_device", "Sin dispositivo registrado"],
  ["permission_denied", "Permiso denegado"],
  ["invalid_token", "Token inválido"],
  ["delivery_unverified", "Entrega no verificada"],
  ["failure", "Fallo de notificación"],
];

const PROTECTION_OPTIONS = [
  ["active_qr", "QR activo"],
  ["inactive_qr", "QR inactivo"],
  ["notices_paused", "Avisos pausados"],
  ["none", "Sin protecciones"],
];

const SOURCE_OPTIONS = [
  ["WEB", "Web"],
  ["ANDROID", "App Android"],
  ["IOS", "App iOS"],
  ["UNKNOWN_LEGACY", "Origen no determinado"],
];

const SOURCE_ICON = { WEB: Monitor, ANDROID: Smartphone, IOS: Smartphone, UNKNOWN_LEGACY: HelpCircle };

const chipCls = (tone) =>
  ({
    good: "bg-emerald-500/15 text-emerald-300 ring-emerald-400/25",
    warn: "bg-amber-500/15 text-amber-200 ring-amber-400/25",
    bad: "bg-rose-500/15 text-rose-300 ring-rose-400/25",
    mute: "bg-white/5 text-slate-300 ring-white/10",
  }[tone]);

const commercialTone = (c) =>
  c === "active" ? "good"
    : ["payment_pending", "payment_under_review", "renewal_under_review"].includes(c) ? "warn"
      : c === "none" ? "mute" : "bad";
const communicationTone = (c) =>
  c === "operational" ? "good" : c === "delivery_unverified" ? "warn" : c === "no_device" ? "mute" : "bad";
const protectionTone = (c) =>
  c === "active_qr" ? "good" : c === "notices_paused" ? "warn" : c === "none" ? "mute" : "bad";

const Chip = ({ tone, children, testid }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${chipCls(tone)}`}
    data-testid={testid}
  >
    {children}
  </span>
);

const fmt = (iso, f = "d MMM yyyy") => {
  if (!iso) return "—";
  try { return format(new Date(iso), f, { locale: es }); } catch (_) { return "—"; }
};

const money = (amount, currency) => {
  if (amount === null || amount === undefined) return "—";
  const symbols = { PEN: "S/ ", INR: "₹", USD: "$ ", EUR: "€ ", ARS: "$ ", BRL: "R$ " };
  return `${symbols[currency] || `${currency} `}${amount}`;
};

const expiryCls = (r) => {
  if (!r.expires_at) return "text-slate-500";
  if (r.commercial_status === "expired" || r.days_remaining === 0) return "text-rose-300";
  if (r.days_remaining < 30) return "text-amber-200";
  return "text-emerald-300";
};

export default function AdminUsersCenter() {
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const debouncedSearch = useDebounce(search, 300);
  const [commercial, setCommercial] = useState(searchParams.get("commercial") || "");
  const [communication, setCommunication] = useState(searchParams.get("communication") || "");
  const [protection, setProtection] = useState(searchParams.get("protection") || "");
  const [sourceFilter, setSourceFilter] = useState(searchParams.get("source") || "");
  const [countryFilter, setCountryFilter] = useState(searchParams.get("country") || "");
  const [expSoon, setExpSoon] = useState(searchParams.get("expiring") === "1");
  const [sort, setSort] = useState("created_at");
  const [direction, setDirection] = useState("desc");
  const [hideTestData, setHideTestData] = useState(() => {
    try { return localStorage.getItem("admin_hide_test") !== "false"; } catch (_) { return true; }
  });
  const [onlyTestData, setOnlyTestData] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(searchParams.get("user") || null);

  const queryParams = useMemo(() => {
    const p = { page, page_size: 50, sort, direction };
    if (debouncedSearch) p.search = debouncedSearch;
    if (commercial) p.commercial = commercial;
    if (communication) p.communication = communication;
    if (protection) p.protection = protection;
    if (sourceFilter) p.source = sourceFilter;
    if (countryFilter) p.country = countryFilter;
    if (onlyTestData) p.only_test = "1";
    else if (hideTestData) p.hide_test = "1";
    return p;
  }, [page, sort, direction, debouncedSearch, commercial, communication, protection,
    sourceFilter, countryFilter, hideTestData, onlyTestData]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/uc/users`, { params: queryParams });
      setRows(r.data.users || []);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
    } catch (_e) {
      toast.error("Error al cargar clientes");
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { setPage(1); }, [debouncedSearch, commercial, communication, protection,
    sourceFilter, countryFilter, hideTestData, onlyTestData]);
  useEffect(() => {
    try { localStorage.setItem("admin_hide_test", String(hideTestData)); } catch (_e) { /* ignore */ }
  }, [hideTestData]);

  const exportCsv = async () => {
    try {
      const { page: _p, page_size: _s, ...rest } = queryParams;
      const r = await axios.get(`${API}/admin/uc/users/export`, { params: rest, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "avisocar_clientes.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exportado (acción auditada)");
    } catch (_e) {
      toast.error("Error al exportar CSV");
    }
  };

  const [revivingId, setRevivingId] = useState(null);
  const reviveUser = async (userId) => {
    setRevivingId(userId);
    try {
      const r = await axios.post(`${API}/admin/uc/users/${userId}/revive`);
      if (r.data.rate_limited) toast.info("Ya se envió un aviso en las últimas 24 h; el banner sigue activo");
      else if ((r.data.push?.sent || 0) > 0) toast.success("Aviso de reactivación enviado");
      else toast.warning("El dispositivo no respondió; el banner quedó activo en su panel");
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al reactivar avisos");
    } finally {
      setRevivingId(null);
    }
  };

  const activeFilters = [
    commercial && ["Estado comercial", COMMERCIAL_OPTIONS.find(([k]) => k === commercial)?.[1], () => setCommercial("")],
    communication && ["Comunicación", COMMUNICATION_OPTIONS.find(([k]) => k === communication)?.[1], () => setCommunication("")],
    protection && ["Protección", PROTECTION_OPTIONS.find(([k]) => k === protection)?.[1], () => setProtection("")],
    sourceFilter && ["Origen", SOURCE_OPTIONS.find(([k]) => k === sourceFilter)?.[1], () => setSourceFilter("")],
    countryFilter && ["País", countryFilter, () => setCountryFilter("")],
    search && ["Búsqueda", search, () => setSearch("")],
    expSoon && ["Vencen ≤30 días", "sí", () => setExpSoon(false)],
  ].filter(Boolean);

  const resetFilters = () => {
    setSearch(""); setCommercial(""); setCommunication(""); setProtection("");
    setSourceFilter(""); setCountryFilter(""); setExpSoon(false); setPage(1);
  };

  const visibleRows = rows.filter(
    (r) => !expSoon || (r.expires_at && r.days_remaining > 0 && r.days_remaining <= 30));

  const selectCls = "h-10 rounded-lg border border-white/15 bg-[#0B2545] px-3 text-sm text-slate-200 focus:border-[#1B84FF] focus:outline-none";

  const sortBy = (field) => {
    if (sort === field) setDirection(direction === "asc" ? "desc" : "asc");
    else { setSort(field); setDirection("desc"); }
  };

  const Th = ({ field, children, className = "" }) => (
    <th scope="col" className={`whitespace-nowrap p-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 ${className}`}>
      {field ? (
        <button onClick={() => sortBy(field)} className="hover:text-slate-200" data-testid={`uc-sort-${field}`}>
          {children}{sort === field ? (direction === "asc" ? " ↑" : " ↓") : ""}
        </button>
      ) : children}
    </th>
  );

  return (
    <AdminShell
      testid="admin-users-center"
      title="Centro de clientes"
      description="Un solo registro por cliente: origen, membresía, importe realmente cobrado, protecciones y estado de avisos."
      actions={
        <>
          <Link to="/admin/users/add">
            <Button className="bg-[#1B84FF] text-white hover:bg-[#0F6FDB]" data-testid="uc-add-user-btn">
              <UserPlus className="mr-2 h-4 w-4" aria-hidden="true" /> Agregar cliente
            </Button>
          </Link>
          <Button variant="outline" onClick={exportCsv} className="border-white/15 text-slate-300" data-testid="uc-export-csv-btn">
            <Download className="mr-2 h-4 w-4" aria-hidden="true" /> CSV
          </Button>
          <Button variant="outline" onClick={fetchRows} className="border-white/15 text-slate-300" data-testid="uc-refresh-btn">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
          </Button>
        </>
      }
    >
      <div className="mb-5 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[240px] flex-1 max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" aria-hidden="true" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nombre, email, teléfono, placa, protección o referencia de pago"
              className="h-10 border-white/15 bg-[#0B2545] pl-9 text-white"
              data-testid="uc-search-input"
            />
          </div>
          <select value={commercial} onChange={(e) => setCommercial(e.target.value)} className={selectCls}
                  aria-label="Estado comercial" data-testid="uc-commercial-filter">
            <option value="">Estado comercial: todos</option>
            {COMMERCIAL_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select value={communication} onChange={(e) => setCommunication(e.target.value)} className={selectCls}
                  aria-label="Estado de comunicación" data-testid="uc-communication-filter">
            <option value="">Comunicación: todas</option>
            {COMMUNICATION_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select value={protection} onChange={(e) => setProtection(e.target.value)} className={selectCls}
                  aria-label="Estado de protección" data-testid="uc-protection-filter">
            <option value="">Protección: todas</option>
            {PROTECTION_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className={selectCls}
                  aria-label="Origen de registro" data-testid="uc-source-filter">
            <option value="">Origen: todos</option>
            {SOURCE_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)} className={selectCls}
                  aria-label="País" data-testid="uc-country-filter">
            <option value="">País: todos</option>
            {COUNTRIES.map((c) => <option key={c.iso} value={c.iso}>{c.flag} {c.name}</option>)}
          </select>
          <Button
            variant="outline"
            onClick={() => setExpSoon((v) => !v)}
            className={expSoon ? "border-amber-400/50 bg-amber-500/10 text-amber-200" : "border-white/15 text-slate-300"}
            data-testid="uc-filter-expiring-btn"
          >
            Vencen ≤30 días
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <label className="flex h-9 cursor-pointer items-center gap-2 rounded-lg border border-white/15 bg-[#0B2545] px-3 text-sm text-slate-300" data-testid="uc-hide-test-toggle">
            <input
              type="checkbox"
              checked={hideTestData}
              disabled={onlyTestData}
              onChange={(e) => setHideTestData(e.target.checked)}
              className="accent-[#1B84FF]"
            />
            Ocultar datos de prueba
          </label>
          <label className="flex h-9 cursor-pointer items-center gap-2 rounded-lg border border-orange-400/30 bg-orange-500/10 px-3 text-sm text-orange-200" data-testid="uc-only-test-toggle">
            <input
              type="checkbox"
              checked={onlyTestData}
              onChange={(e) => setOnlyTestData(e.target.checked)}
              className="accent-orange-400"
            />
            Inspeccionar solo registros de QA
          </label>
          <span className="text-sm text-slate-400" data-testid="uc-result-count">
            {loading ? "Cargando…" : `${total} cliente${total === 1 ? "" : "s"}`}
            {pages > 1 && !loading ? ` · página ${page} de ${pages}` : ""}
          </span>
          {activeFilters.length > 0 && (
            <Button variant="ghost" onClick={resetFilters} className="h-9 text-slate-400 hover:text-white" data-testid="uc-reset-filters">
              Limpiar filtros
            </Button>
          )}
        </div>

        {activeFilters.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="uc-active-filters">
            {activeFilters.map(([label, value, clear]) => (
              <button
                key={label}
                onClick={clear}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#1B84FF]/15 px-3 py-1 text-xs text-sky-200 ring-1 ring-inset ring-[#1B84FF]/30 hover:bg-[#1B84FF]/25"
              >
                {label}: {value}
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="overflow-x-auto rounded-xl border border-white/10 bg-[#0B2545]">
        {loading ? (
          <div className="p-12 text-center text-slate-400">
            <Loader2 className="mr-2 inline h-5 w-5 animate-spin" aria-hidden="true" />Cargando clientes…
          </div>
        ) : visibleRows.length === 0 ? (
          <div className="p-12 text-center" data-testid="uc-empty">
            <p className="font-medium text-slate-300">Ningún cliente coincide con estos filtros</p>
            <p className="mt-1 text-sm text-slate-500">
              Prueba con otro término de búsqueda o limpia los filtros activos.
            </p>
            {activeFilters.length > 0 && (
              <Button variant="outline" onClick={resetFilters} className="mt-4 border-white/15 text-slate-300">
                Limpiar filtros
              </Button>
            )}
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="uc-users-table">
            <caption className="sr-only">Clientes de AvisoCar</caption>
            <thead className="border-b border-white/10">
              <tr>
                <Th field="full_name">Cliente</Th>
                <Th field="registration_source">Origen</Th>
                <Th field="country_code">País</Th>
                <Th field="commercial_status">Estado comercial</Th>
                <Th field="plan_months">Plan</Th>
                <Th field="amount_paid">Pagado</Th>
                <Th field="expires_at">Vigencia</Th>
                <Th field="vehicles_active">Protecciones</Th>
                <Th field="communication_status">Avisos</Th>
                <Th field="last_login_at">Última actividad</Th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r) => {
                const SrcIcon = SOURCE_ICON[r.registration_source] || Globe;
                return (
                  <tr
                    key={r.user_id}
                    onClick={() => setSelectedUserId(r.user_id)}
                    className="cursor-pointer border-b border-white/5 transition-colors hover:bg-white/5"
                    data-testid={`uc-row-${r.user_id}`}
                  >
                    <td className="p-3">
                      <p className="font-medium text-white">{r.full_name}</p>
                      <p className="text-xs text-slate-500">{r.email}</p>
                      {r.is_qa_record && (
                        <span className="mt-1 inline-block rounded bg-orange-500/15 px-1.5 text-[10px] font-bold text-orange-300">
                          QA
                        </span>
                      )}
                      {r.attention_reason && r.commercial_status === "active" && (
                        <p className="mt-1 text-[11px] text-amber-200" data-testid={`uc-attention-${r.user_id}`}>
                          Atención: {r.attention_reason}
                        </p>
                      )}
                    </td>
                    <td className="p-3" data-testid={`uc-source-${r.user_id}`}>
                      <span className="inline-flex items-center gap-1.5 text-xs text-slate-300">
                        <SrcIcon className="h-3.5 w-3.5 text-[#5BC0FF]" aria-hidden="true" />
                        {r.registration_source_label}
                      </span>
                      {r.last_platform_label && r.last_platform !== r.registration_source && (
                        <p className="mt-0.5 text-[10px] text-slate-500">Último uso: {r.last_platform_label}</p>
                      )}
                      <p className="mt-0.5 text-[10px] text-slate-600">Alta: {fmt(r.created_at)}</p>
                    </td>
                    <td className="p-3 text-slate-300">
                      {r.country_code && /^[A-Z]{2}$/.test(r.country_code)
                        ? String.fromCodePoint(...[...r.country_code].map((c) => 127397 + c.charCodeAt(0)))
                        : ""} {r.country_code}
                      <p className="text-[10px] uppercase text-slate-600">{r.language || ""}</p>
                    </td>
                    <td className="p-3">
                      <Chip tone={commercialTone(r.commercial_status)} testid={`uc-commercial-${r.user_id}`}>
                        {r.commercial_status_label}
                      </Chip>
                      {r.last_payment_ref && (
                        <p className="mt-1 font-mono text-[10px] text-slate-500">Ref. {r.last_payment_ref}</p>
                      )}
                    </td>
                    <td className="p-3 text-slate-300">{r.plan_name}</td>
                    <td className="p-3" data-testid={`uc-paid-${r.user_id}`}>
                      <p className="font-medium text-white">{money(r.amount_paid, r.amount_paid_currency)}</p>
                      <p className="text-[10px] text-slate-500">
                        {r.amount_paid === null || r.amount_paid === undefined
                          ? "Sin pago aprobado"
                          : `Cobrado el ${fmt(r.amount_paid_at)}`}
                      </p>
                    </td>
                    <td className={`p-3 font-medium ${expiryCls(r)}`}>
                      {fmt(r.expires_at)}
                      {r.expires_at && <p className="text-[10px] opacity-70">{r.days_remaining} días restantes</p>}
                      {r.paid_at && <p className="text-[10px] text-slate-600">Inicio: {fmt(r.paid_at)}</p>}
                    </td>
                    <td className="p-3">
                      <Chip tone={protectionTone(r.protection_status)} testid={`uc-protection-${r.user_id}`}>
                        {r.protection_status_label}
                      </Chip>
                      <p className="mt-1 text-[10px] text-slate-500">{r.vehicles_active} de {r.vehicles} con QR activo</p>
                    </td>
                    <td className="p-3">
                      <Chip tone={communicationTone(r.communication_status)} testid={`uc-communication-${r.user_id}`}>
                        {r.communication_status_label}
                      </Chip>
                      <p className="mt-1 text-[10px] text-slate-500">{r.push_devices} dispositivo(s)</p>
                      {r.push_stale_devices > 0 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); reviveUser(r.user_id); }}
                          disabled={revivingId === r.user_id}
                          className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-rose-400/40 bg-rose-500/15 px-2 py-0.5 text-[11px] text-rose-200 hover:bg-rose-500/25 disabled:opacity-50"
                          title="Envía un aviso de sistema y activa el banner de reactivación en su panel"
                          data-testid={`uc-revive-btn-${r.user_id}`}
                        >
                          {revivingId === r.user_id
                            ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                            : <HeartPulse className="h-3 w-3" aria-hidden="true" />}
                          Reactivar avisos
                        </button>
                      )}
                    </td>
                    <td className="p-3 text-xs text-slate-400">{fmt(r.last_login_at, "d MMM yyyy HH:mm")}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {pages > 1 && (
        <div className="mt-4 flex items-center justify-between" data-testid="uc-pagination">
          <Button
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="border-white/15 text-slate-300"
            data-testid="uc-prev-page"
          >
            <ChevronLeft className="mr-1 h-4 w-4" aria-hidden="true" /> Anterior
          </Button>
          <span className="text-sm text-slate-400">Página {page} de {pages}</span>
          <Button
            variant="outline"
            disabled={page >= pages}
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            className="border-white/15 text-slate-300"
            data-testid="uc-next-page"
          >
            Siguiente <ChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      )}

      {selectedUserId && (
        <UserDrawer
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
          onChanged={fetchRows}
        />
      )}
    </AdminShell>
  );
}
